import React, { useState } from "react";
import { Modal, Form, Input, Button, Alert, Space, Typography, Tag } from "antd";
import { LockOutlined, BankOutlined, UserOutlined, WarningOutlined } from "@ant-design/icons";
import PasswordStrengthMeter from "../common/PasswordStrengthMeter";
import { updateEmployeePassword } from "../../services/employeeService";
import { updateCustomerPassword } from "../../services/customerService";

const { Text } = Typography;

/**
 * Birleşik şifre değiştirme modal'ı
 * @param {Object} props
 * @param {boolean} props.visible - Modal görünürlüğü
 * @param {Function} props.onCancel - İptal callback
 * @param {Function} props.onSuccess - Başarı callback
 * @param {Object} props.entity - Kullanıcı/müşteri/çalışan objesi
 * @param {string} props.entityType - Varlık tipi ("employee" | "customer")
 */
const ChangePasswordModal = ({ visible, onCancel, onSuccess, entity, entityType = "employee" }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");

  const isCustomer = entityType === "customer";
  const minLength = isCustomer ? 8 : 6;
  const maxLength = isCustomer ? 50 : 20;

  const config = {
    employee: {
      title: "Çalışan Şifresi Değiştir",
      icon: <UserOutlined style={{ color: "#1890ff" }} />,
      getDisplayName: (e) => `${e?.name} ${e?.surname} (@${e?.username})`,
      alertType: "info",
      showSecurityWarning: false,
      updateFn: updateEmployeePassword,
    },
    customer: {
      title: "Müşteri Şifresi Değiştir",
      icon: <BankOutlined style={{ color: "#1890ff" }} />,
      getDisplayName: (e) => e?.companyName,
      getSubInfo: (e) => `Email: ${e?.email}`,
      alertType: "info",
      showSecurityWarning: true,
      updateFn: updateCustomerPassword,
    },
  };

  const currentConfig = config[entityType];

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await currentConfig.updateFn(entity.id, {
        newPassword: values.newPassword,
      });

      if (response.data) {
        onSuccess();
        resetForm();
      }
    } catch (error) {
      console.error("Şifre güncelleme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setPassword("");
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          {currentConfig.icon}
          <span>{currentConfig.title}</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={isCustomer ? 550 : 500}
      centered
    >
      {entity && (
        <Alert
          message={
            isCustomer ? (
              <Space direction="vertical" size={0}>
                <Text strong>{currentConfig.getDisplayName(entity)}</Text>
                {currentConfig.getSubInfo && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {currentConfig.getSubInfo(entity)}
                  </Text>
                )}
              </Space>
            ) : (
              currentConfig.getDisplayName(entity)
            )
          }
          type={currentConfig.alertType}
          showIcon
          icon={isCustomer ? <BankOutlined /> : undefined}
          style={{ marginBottom: 20 }}
        />
      )}

      {currentConfig.showSecurityWarning && (
        <Alert
          message="Güvenlik Uyarısı"
          description={`Müşteri hesapları için güçlü şifre kullanımı önemlidir. En az ${minLength} karakter kullanın ve mümkünse rakam, büyük/küçük harf ve özel karakterler ekleyin.`}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 20 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="newPassword"
          label={
            isCustomer ? (
              <Space>
                <span>Yeni Şifre</span>
                <Tag color="red">Zorunlu</Tag>
              </Space>
            ) : (
              "Yeni Şifre"
            )
          }
          rules={[
            { required: true, message: "Lütfen yeni şifre girin!" },
            { min: minLength, message: `Şifre en az ${minLength} karakter olmalıdır!` },
            { max: maxLength, message: `Şifre en fazla ${maxLength} karakter olabilir!` },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={`En az ${minLength} karakter`}
            onChange={handlePasswordChange}
            autoComplete="new-password"
          />
        </Form.Item>

        <PasswordStrengthMeter
          password={password}
          minLength={minLength}
          isCustomer={isCustomer}
          showRequirements={true}
        />

        <Form.Item
          name="confirmPassword"
          label="Şifre Tekrarı"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Lütfen şifreyi tekrar girin!" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Şifreler eşleşmiyor!"));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Şifreyi tekrar girin"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button onClick={handleCancel}>İptal</Button>
            <Button type="primary" htmlType="submit" loading={loading} icon={<LockOutlined />}>
              Şifreyi Güncelle
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
